import { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { FileText, Plus, X, ExternalLink, Download, Printer, Image as ImageIcon, CreditCard, Pencil, Trash2 } from "lucide-react";
import ImageUploadField from "../../components/ImageUploadField";

const PREFIX = { Diamond: "DIAM", Sapphire: "SAPP", Ruby: "RUBY", Emerald: "EMER", Rare: "RARE" };

const EMPTY = {
  certificate_number: "",
  gemstone_name: "",
  category: "Diamond",
  carat: "",
  weight_unit: "ct",
  dimensions: "",
  color: "",
  origin: "",
  treatment: "None / Untreated",
  issuing_lab: "OPIK Gemological Bureau",
  issued_date: new Date().toISOString().slice(0, 10),
  owner_name: "",
  warranty_status: "Active - Lifetime",
  insurance_eligible: true,
  photo_url: "",
  signatory_name: "Mr. Tauf",
  signatory_title: "Authorized Officer",
  signature_image_url: "",
  shape_cut: "",
  metal: "",
  comment: "",
};

const WEIGHT_UNITS = [
  { v: "ct", l: "Carat (ct)" },
  { v: "g", l: "Gram (g)" },
  { v: "kg", l: "Kilogram (kg)" },
  { v: "pc", l: "Piece (pc)" },
];

const SEED_LABS = [
  "OPIK Gemological Bureau",
  "GIA",
  "Gübelin",
  "SSEF",
  "AGL",
  "GRS",
  "AIGS",
  "Lotus Gemology",
  "GCAL",
  "IGI",
];

const SEED_CATS = ["Diamond", "Sapphire", "Ruby", "Emerald", "Tourmaline", "Spinel", "Tanzanite", "Opal", "Pearl", "Jade", "Rare"];

const CertificateGenerator = () => {
  const [certs, setCerts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  // When non-null, the modal is in "edit" mode against this certificate_number.
  // We keep the original number to issue the PUT to the right resource and
  // also to mark the certificate_number field as read-only (immutable in edit).
  const [editingNumber, setEditingNumber] = useState(null);
  const [filter, setFilter] = useState("");

  const load = () => api.get("/certificates").then(({ data }) => setCerts(data));
  useEffect(() => { load(); }, []);

  const buildNumber = (cat) => {
    const prefix = PREFIX[cat] || (cat || "GEM").slice(0, 4).toUpperCase().replace(/[^A-Z]/g, "");
    const seq = (certs.filter((c) => c.certificate_number?.startsWith(`OPK-${prefix}`)).length + 1)
      .toString().padStart(3, "0");
    return `OPK-${prefix}-${seq}`;
  };

  const startCreate = () => {
    setEditingNumber(null);
    setForm({ ...EMPTY, certificate_number: buildNumber("Diamond") });
    setOpen(true);
  };

  const startEdit = (cert) => {
    // Pre-fill the form with the existing certificate's data so the admin
    // can tweak any field except the number itself (which is bonded to the
    // public QR code and printed COA copies).
    setEditingNumber(cert.certificate_number);
    setForm({
      ...EMPTY,
      ...cert,
      issued_date: (cert.issued_date || "").slice(0, 10) || EMPTY.issued_date,
      carat: cert.carat ?? "",
      photo_url: cert.photo_url || "",
      signature_image_url: cert.signature_image_url || "",
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        carat: parseFloat(form.carat) || 0,
        photo_url: form.photo_url || null,
        signature_image_url: form.signature_image_url || null,
        dimensions: form.dimensions || null,
        color: form.color || null,
      };
      if (editingNumber) {
        await api.put(`/certificates/${editingNumber}`, payload);
        toast.success(`Certificate ${editingNumber} updated.`);
      } else {
        await api.post("/certificates", payload);
        toast.success(`Certificate ${form.certificate_number} issued.`);
      }
      setOpen(false); setEditingNumber(null); setForm(EMPTY); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };

  const remove = async (cert_number) => {
    if (!window.confirm(`Delete ${cert_number}? Public QR verifications for this number will return 404 going forward.`)) return;
    try {
      await api.delete(`/certificates/${cert_number}`);
      toast.success(`${cert_number} deleted.`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  const onCategoryChange = (cat) => {
    setForm({ ...form, category: cat, certificate_number: buildNumber(cat) });
  };

  const downloadCOA = (cert_number) => {
    const url = `/api/certificates/${cert_number}/coa.pdf`;
    window.open(url, "_blank");
  };

  const downloadCOAPrint = (cert_number) => {
    const url = `/api/certificates/${cert_number}/coa-print.pdf`;
    window.open(url, "_blank");
  };

  const downloadCOAFrontPrint = (cert_number) => {
    // Print-ready single-page front only — used when the back is already
    // pre-produced on the physical card stock.
    const url = `/api/certificates/${cert_number}/coa-print.pdf?side=front`;
    window.open(url, "_blank");
  };

  const downloadCOAImage = (cert_number) => {
    const url = `/api/certificates/${cert_number}/coa.jpg`;
    window.open(url, "_blank");
  };

  const filtered = certs.filter((c) => {
    const q = filter.toLowerCase();
    return !q || c.certificate_number.toLowerCase().includes(q) ||
      c.gemstone_name?.toLowerCase().includes(q) || c.owner_name?.toLowerCase().includes(q);
  });

  return (
    <div data-testid="cert-generator" className="pb-32">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <input data-testid="cert-search" placeholder="Search by number, stone, or owner…" value={filter} onChange={(e) => setFilter(e.target.value)}
          className="bg-transparent border border-white/20 px-4 py-3 text-white focus:outline-none focus:border-gold w-full md:w-96" />
        <button data-testid="cert-new-btn" onClick={startCreate} className="bg-gold text-black font-accent uppercase tracking-[0.24em] text-xs px-6 py-3 hover:bg-ivory flex items-center gap-2">
          <Plus size={14} /> Issue Certificate
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-white/40 font-accent text-[10px] tracking-[0.28em] uppercase border-b border-white/10">
            <tr><th className="text-left py-4">Number</th><th className="text-left">Stone</th><th className="text-left">Weight</th><th className="text-left">Origin</th><th className="text-left">Lab</th><th className="text-left">Owner</th><th className="text-left">Warranty</th><th></th></tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id || c.certificate_number} data-testid={`cert-row-${c.certificate_number}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                <td className="py-4 font-accent text-gold tracking-[0.18em]">{c.certificate_number}</td>
                <td className="font-heading text-lg">{c.gemstone_name}</td>
                <td>{c.carat} {c.weight_unit || "ct"}</td>
                <td>{c.origin}</td>
                <td className="text-white/60">{c.issuing_lab}</td>
                <td>{c.owner_name || <span className="text-white/40">Unassigned</span>}</td>
                <td className="text-white/60">{c.warranty_status}</td>
                <td className="text-right">
                  <button data-testid={`cert-edit-${c.certificate_number}`} onClick={() => startEdit(c)}
                    title="Edit certificate"
                    className="text-white/60 hover:text-gold p-2 inline-block">
                    <Pencil size={14} />
                  </button>
                  <button data-testid={`cert-coa-${c.certificate_number}`} onClick={() => downloadCOA(c.certificate_number)}
                    title="Download Warranty Card PDF"
                    className="text-white/60 hover:text-gold p-2 inline-block">
                    <Download size={14} />
                  </button>
                  <button data-testid={`cert-coa-print-${c.certificate_number}`} onClick={() => downloadCOAPrint(c.certificate_number)}
                    title="Print-ready PDF · both sides (CMYK bleed + trim marks)"
                    className="text-white/60 hover:text-gold p-2 inline-block">
                    <Printer size={14} />
                  </button>
                  <button data-testid={`cert-coa-front-${c.certificate_number}`} onClick={() => downloadCOAFrontPrint(c.certificate_number)}
                    title="Print-ready PDF · front only (back is already on card stock)"
                    className="text-white/60 hover:text-gold p-2 inline-block">
                    <CreditCard size={14} />
                  </button>
                  <button data-testid={`cert-coa-jpg-${c.certificate_number}`} onClick={() => downloadCOAImage(c.certificate_number)}
                    title="Download JPEG (WhatsApp / phone print)"
                    className="text-white/60 hover:text-gold p-2 inline-block">
                    <ImageIcon size={14} />
                  </button>
                  <a data-testid={`cert-view-${c.certificate_number}`} href={`/verify?cert=${c.certificate_number}`} target="_blank" rel="noreferrer" className="text-white/60 hover:text-gold p-2 inline-block">
                    <ExternalLink size={14} />
                  </a>
                  <button data-testid={`cert-delete-${c.certificate_number}`} onClick={() => remove(c.certificate_number)}
                    title="Delete certificate"
                    className="text-white/40 hover:text-red-400 p-2 inline-block">
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="text-white/40 py-12 text-center">No certificates match.</div>}
      </div>

      {open && (
        <div className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm overflow-y-auto" onClick={() => { setOpen(false); setEditingNumber(null); }}>
          <div className="max-w-2xl mx-auto my-12 bg-noir border border-gold/40 p-8 md:p-10" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-8">
              <div className="flex items-center gap-3">
                <FileText className="text-gold" size={24} />
                <h2 className="font-heading text-3xl">{editingNumber ? `Edit Certificate · ${editingNumber}` : "Issue Certificate of Authenticity"}</h2>
              </div>
              <button onClick={() => { setOpen(false); setEditingNumber(null); }} className="text-white/60 hover:text-white"><X size={20} /></button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Inp label="Certificate Number" v={form.certificate_number} on={(v) => setForm({...form, certificate_number: v.toUpperCase()})} testid="cert-f-num" readOnly={!!editingNumber} />
              <Combo label="Category (custom)" v={form.category} on={onCategoryChange} options={SEED_CATS} placeholder="Diamond, Tourmaline, Opal…" testid="cert-f-cat" listId="coa-cat" />
              <Inp label="Gemstone Name" v={form.gemstone_name} on={(v) => setForm({...form, gemstone_name: v})} testid="cert-f-name" />
              <Inp label="Color (e.g. pigeon blood)" v={form.color} on={(v) => setForm({...form, color: v})} testid="cert-f-color" />
              <Inp label="Weight" type="number" step="0.01" v={form.carat} on={(v) => setForm({...form, carat: v})} testid="cert-f-carat" />
              <Inp label="Weight Unit" v={form.weight_unit} on={(v) => setForm({...form, weight_unit: v})}
                options={WEIGHT_UNITS.map((u) => ({ value: u.v, label: u.l }))} testid="cert-f-unit" />
              <Inp label="Dimensions (e.g. 12 × 12 × 6)" v={form.dimensions} on={(v) => setForm({...form, dimensions: v})} testid="cert-f-dim" />
              <Inp label="Origin" v={form.origin} on={(v) => setForm({...form, origin: v})} testid="cert-f-origin" />
              <Inp label="Treatment" v={form.treatment} on={(v) => setForm({...form, treatment: v})} testid="cert-f-treat" />
              <Combo label="Issuing Lab (custom)" v={form.issuing_lab} on={(v) => setForm({...form, issuing_lab: v})}
                options={SEED_LABS} placeholder="GIA, Gübelin, or any lab name…" testid="cert-f-lab" listId="coa-lab" />
              <Inp label="Issued Date" type="date" v={form.issued_date} on={(v) => setForm({...form, issued_date: v})} testid="cert-f-date" />
              <Inp label="Warranty" v={form.warranty_status} on={(v) => setForm({...form, warranty_status: v})}
                options={[{value:"Active - Lifetime", label:"Active - Lifetime"},{value:"Active - 10 Years",label:"Active - 10 Years"},{value:"Active - 5 Years",label:"Active - 5 Years"},{value:"Expired",label:"Expired"}]} testid="cert-f-warranty" />
              <Inp label="Owner (optional)" v={form.owner_name} on={(v) => setForm({...form, owner_name: v})} testid="cert-f-owner" />
            </div>

            <div className="mt-6 pt-6 border-t border-white/10">
              <div className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-4">Warranty Card Fields (optional)</div>
              <div className="grid grid-cols-2 gap-4">
                <Inp label="Shape / Cut (e.g. Round Brilliant)" v={form.shape_cut} on={(v) => setForm({...form, shape_cut: v})} testid="cert-f-shape" />
                <Inp label="Metal (e.g. 18K Yellow Gold)" v={form.metal} on={(v) => setForm({...form, metal: v})} testid="cert-f-metal" />
                <div className="col-span-2">
                  <Inp label="Comment (short note printed under Color Grade)" v={form.comment} on={(v) => setForm({...form, comment: v})} testid="cert-f-comment" />
                </div>
              </div>
              <div className="mt-3 text-[10px] tracking-[0.18em] font-accent uppercase text-white/40 italic">
                Clarity Grade, Cut Grade and Setting have been retired from the warranty card and are no longer shown or printed.
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-white/10">
              <div className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-4">Certificate Visuals</div>
              <div className="grid grid-cols-1 gap-6">
                <ImageUploadField
                  label="Product Photo"
                  value={form.photo_url}
                  onChange={(v) => setForm({...form, photo_url: v})}
                  testid="cert-f-photo"
                  helper="Centerpiece photo printed on the warranty card. Upload from device (mobile camera OK) — it's saved to secure cloud storage and a URL is used. Click Crop to fit the 1:1 card frame."
                  cropAspect={1}
                  autoCropOnUpload={true}
                  hosted={true}
                  uploadTag="warranty-photo"
                  visibility="public"
                />
                <div className="grid grid-cols-2 gap-4">
                  <Inp label="Signatory Name" v={form.signatory_name} on={(v) => setForm({...form, signatory_name: v})} testid="cert-f-signname" />
                  <Inp label="Signatory Title" v={form.signatory_title} on={(v) => setForm({...form, signatory_title: v})} testid="cert-f-signtitle" />
                </div>
                <ImageUploadField
                  label="Signature Image (optional)"
                  value={form.signature_image_url}
                  onChange={(v) => setForm({...form, signature_image_url: v})}
                  testid="cert-f-signimg"
                  helper="Transparent PNG of a handwritten signature. Overrides the procedural cursive flourish."
                />
              </div>
            </div>

            <label className="flex items-center gap-3 mt-4 cursor-pointer">
              <input data-testid="cert-f-insur" type="checkbox" checked={!!form.insurance_eligible} onChange={(e) => setForm({...form, insurance_eligible: e.target.checked})} className="accent-gold w-4 h-4" />
              <span className="text-white/70 text-sm">Eligible for insurance underwriting</span>
            </label>
            <div className="flex gap-3 mt-8">
              <button data-testid="cert-save-btn" onClick={save} className="bg-gold text-black font-accent uppercase tracking-[0.24em] text-xs px-7 py-3 hover:bg-ivory">{editingNumber ? "Save Changes" : "Issue"}</button>
              <button onClick={() => { setOpen(false); setEditingNumber(null); }} className="border border-white/30 text-white font-accent uppercase tracking-[0.24em] text-xs px-7 py-3 hover:border-white">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Inp = ({ label, v, on, type = "text", step, options, testid, placeholder, readOnly = false }) => (
  <div>
    <label className="text-white/50 text-xs tracking-[0.18em] font-accent uppercase mb-2 block">{label}{readOnly && <span className="text-gold/60 ml-2 normal-case tracking-normal text-[10px]">· locked</span>}</label>
    {options ? (
      <select data-testid={testid} value={v || ""} onChange={(e) => on(e.target.value)}
        className="w-full bg-transparent border border-white/20 px-3 py-3 text-white focus:outline-none focus:border-gold">
        {options.map((o) => <option key={o.value || o} value={o.value || o} className="bg-noir">{o.label || o}</option>)}
      </select>
    ) : (
      <input data-testid={testid} type={type} step={step} value={v || ""} placeholder={placeholder} readOnly={readOnly} onChange={(e) => on(e.target.value)}
        className={`w-full bg-transparent border border-white/20 px-3 py-3 text-white focus:outline-none focus:border-gold ${readOnly ? "opacity-60 cursor-not-allowed" : ""}`} />
    )}
  </div>
);

const Combo = ({ label, v, on, options, placeholder, testid, listId }) => (
  <div>
    <label className="text-white/50 text-xs tracking-[0.18em] font-accent uppercase mb-2 block">{label}</label>
    <input data-testid={testid} list={listId} value={v || ""} placeholder={placeholder} onChange={(e) => on(e.target.value)}
      className="w-full bg-transparent border border-white/20 px-3 py-3 text-white focus:outline-none focus:border-gold" />
    <datalist id={listId}>{options.map((o) => <option key={o} value={o} />)}</datalist>
  </div>
);

export default CertificateGenerator;
